import { Device } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";

export interface UseDevicesInGroupOptions {
  /** Refetch interval in ms; `false` turns polling off. Default 60 s. */
  refetchInterval?: number | false;
}

/**
 * Active devices in the given group, including devices in its subgroups, with only `fields`
 * loaded. Stays disabled until a `groupId` is known. Errors are not swallowed.
 *
 * `isTruncated` is true when the result filled a whole {@link RESULTS_LIMIT_DEVICE} page, so more
 * devices may exist than were returned. The caller decides whether that is a warning or an error.
 * Pass `fields` as a stable reference (a module constant); it is part of the query key.
 */
export function useDevicesInGroup<T extends Device = Device>(
  groupId: string | null | undefined,
  fields: string[],
  { refetchInterval = 60_000 }: UseDevicesInGroupOptions = {},
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const query = useQuery<T[]>({
    queryKey: ["devicesInGroup", activeSessionId, groupId, fields],
    enabled: !!groupId,
    queryFn: async ({ signal }) => {
      const result = await geotabClient.call(
        "Get",
        {
          typeName: "Device",
          search: { fromDate: new Date(), groupFilterCondition: { groupId } },
          propertySelector: { fields, isIncluded: true },
          resultsLimit: RESULTS_LIMIT_DEVICE,
        },
        signal,
      );
      return (result as T[] | null) ?? [];
    },
    refetchInterval,
    placeholderData: keepPreviousData,
  });

  // Object.assign keeps TanStack's tracked result: a spread would read every field and re-render
  // the caller on each fetch-state change.
  return Object.assign(query, { isTruncated: (query.data?.length ?? 0) >= RESULTS_LIMIT_DEVICE });
}
